import type { ReactNode } from 'react';
import { cn } from '../cn.ts';

export interface MockFrameProps {
  readonly children: ReactNode;
  /**
   * Pill URL ở giữa thanh chrome. Ẩn dưới `lg` — 11px mono ở khổ đó không đọc được và không mang
   * thông tin nào (`reference.md` §2.1).
   */
  readonly urlLabel?: string;
  /**
   * Ẩn trọn khung khỏi cây trợ năng — dùng khi khung là HÌNH MINH HOẠ, tức nội dung bên trong
   * không mang thông tin nào mà chữ xung quanh chưa nói.
   *
   * Vì sao là prop CÓ TÊN chứ không phải để phía gọi tự đặt thuộc tính gạch ngang: TypeScript
   * loại thuộc tính JSX có dấu gạch ngang khỏi phép kiểm thừa-thuộc-tính, nên một thuộc tính như
   * vậy đặt lên component do người viết đi qua `typecheck` màu xanh rồi biến mất lúc render. Đã
   * xảy ra thật: hero truyền thuộc tính ẩn cho khung này, `MockFrameProps` không khai và cũng
   * không trải rest, và 1299 ký tự UI giả bị screen reader đọc ngay sau `h1` ở cả hai locale.
   * Prop viết liền thì gõ sai là `typecheck` đỏ ngay — đo được, không phải suy đoán.
   */
  readonly decorative?: boolean;
  readonly className?: string;
}

/**
 * Khung "cửa sổ app" của mock: thanh chrome 40px với 3 chấm + pill URL, rồi thân.
 *
 * Ẩn khỏi trợ năng hay không là quyết định của PHÍA GỌI, khai qua `decorative`: mock hero là hình
 * minh hoạ (h1 + lead phía trên đã nói đủ), còn một mock mang nội dung thật thì phải đọc được.
 * Đặt cứng ở đây thì hộ dùng thứ hai không gỡ ra được.
 */
export function MockFrame({ children, urlLabel, decorative, className }: MockFrameProps) {
  return (
    <div
      aria-hidden={decorative === true ? true : undefined}
      className={cn('overflow-hidden rounded-frame bg-surface shadow-hero-frame', className)}
    >
      <div className="flex h-10 items-center gap-2 border-b border-ink-100 bg-surface-muted px-3.5">
        <span className="size-2.5 rounded-pill bg-ink-200" />
        <span className="size-2.5 rounded-pill bg-ink-200" />
        <span className="size-2.5 rounded-pill bg-ink-200" />
        {urlLabel !== undefined && (
          <span className="mx-auto hidden rounded-pill border border-ink-100 bg-surface px-3.5 py-1 font-mono text-micro text-ink-500 lg:block">
            {urlLabel}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}
